import { type ReactNode } from "react";
import { Controller, useFormContext, useWatch, type FieldPathByValue } from "react-hook-form";

import { Box } from "ui/box";
import { Skeleton } from "ui/skeleton";
import { Typography } from "ui/typography";
import { ColorField } from "containers/admin/clients/client/theme-configuration-page/components/color-field/color-field";
import { FieldSkeleton } from "containers/admin/clients/client/theme-configuration-page/components/field-skeleton/field-skeleton";

import {
  BRAND_BASE_STOP,
  BRAND_SHADE_STOPS,
  generateBrandShades,
  generateNeutralShades,
  isHexColor,
  NEUTRAL_BASE_STOP,
  NEUTRAL_SHADE_STOPS,
} from "containers/admin/clients/client/theme-configuration-page/helpers/color-shades";
import { validateHexColor } from "containers/admin/clients/client/theme-configuration-page/brand-identity/colors-tab/colors-tab.helpers";

import { CONTROL_HEIGHT } from "theme/components/field-geometry";

import type { BrandIdentityFormValues } from "containers/admin/clients/client/theme-configuration-page/brand-identity/brand-identity.types";

import {
  COLOR_ROW_SX,
  FIELD_SX,
  ROWS_SX,
  SECTION_SX,
  SHADE_SX,
  SHADES_SX,
  ShadeIndicator,
  ShadeSwatch,
  TAB_SX,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/colors-tab/colors-tab.styles";
import {
  BRAND_COLOR_FIELDS,
  UTILITY_COLOR_FIELDS,
} from "containers/admin/clients/client/theme-configuration-page/brand-identity/colors-tab/colors-tab.constants";

import Icons from "assets";

type ColorsTabProps = {
  isEditing: boolean;
  isLoading: boolean;
};

type ColorInputProps = {
  name: FieldPathByValue<BrandIdentityFormValues, string>;
  label: string;
  isRequired: boolean;
  isEditing: boolean;
};

type ShadesProps = {
  label: string;
  stops: readonly number[];
  /** The stop the entered colour fills, marked so the user can see which one they are typing. */
  baseStop: number;
  shades?: Record<number, string>;
};

const ColorInput = ({ name, label, isRequired, isEditing }: ColorInputProps) => (
  <Controller
    name={name}
    rules={{ validate: validateHexColor(label, isRequired) }}
    render={({ field, fieldState }) => (
      <ColorField
        {...field}
        label={label}
        disabled={!isEditing}
        errMessage={fieldState.error?.message}
        sx={FIELD_SX}
      />
    )}
  />
);

const Shades = ({ label, stops, baseStop, shades }: ShadesProps) => (
  <Box sx={SHADES_SX}>
    {stops.map((stop) => (
      <Box key={stop} sx={SHADE_SX}>
        <Typography variant="body1" component="span">
          {stop}
        </Typography>
        <ShadeSwatch
          role="img"
          aria-label={`${label} ${stop}${shades ? `: ${shades[stop]}` : ""}${shades && stop === baseStop ? " (entered)" : ""}`}
          swatchColor={shades?.[stop]}>
          {shades && stop === baseStop && (
            <ShadeIndicator>
              <Icons.CoreIndicator aria-hidden />
            </ShadeIndicator>
          )}
        </ShadeSwatch>
      </Box>
    ))}
  </Box>
);

const ShadesSkeleton = ({ stops }: { stops: readonly number[] }) => (
  <Box sx={SHADES_SX}>
    {stops.map((stop) => (
      <Box key={stop} sx={SHADE_SX}>
        <Typography variant="body1" component="span">
          {stop}
        </Typography>
        <Skeleton variant="rounded" height={CONTROL_HEIGHT.medium} />
      </Box>
    ))}
  </Box>
);

type SectionProps = {
  title: string;
  children: ReactNode;
};

const Section = ({ title, children }: SectionProps) => (
  <Box component="section" sx={SECTION_SX}>
    <Typography variant="h4" component="h3">
      {title}
    </Typography>
    <Box sx={ROWS_SX}>{children}</Box>
  </Box>
);

const ColorsTabSkeleton = () => (
  <Box aria-busy sx={TAB_SX}>
    <Section title="Brand">
      {BRAND_COLOR_FIELDS.map(({ name, label }) => (
        <Box key={name} sx={COLOR_ROW_SX}>
          <FieldSkeleton label={label} sx={FIELD_SX} />
          <ShadesSkeleton stops={BRAND_SHADE_STOPS} />
        </Box>
      ))}
    </Section>
    <Section title="Neutral">
      <Box sx={COLOR_ROW_SX}>
        <FieldSkeleton label="Black" sx={FIELD_SX} />
        <ShadesSkeleton stops={NEUTRAL_SHADE_STOPS} />
      </Box>
    </Section>
    <Section title="Utility">
      {UTILITY_COLOR_FIELDS.map(({ name, label }) => (
        <FieldSkeleton key={name} label={label} sx={FIELD_SX} />
      ))}
    </Section>
  </Box>
);

export const ColorsTab = ({ isEditing, isLoading }: ColorsTabProps) => {
  const { control } = useFormContext<BrandIdentityFormValues>();
  const { brand, neutral } = useWatch({ control, name: "colors" });

  if (isLoading) {
    return <ColorsTabSkeleton />;
  }

  return (
    <Box sx={TAB_SX}>
      <Section title="Brand">
        {BRAND_COLOR_FIELDS.map(({ name, label }) => (
          <Box key={name} sx={COLOR_ROW_SX}>
            <ColorInput name={`colors.brand.${name}`} label={label} isRequired={false} isEditing={isEditing} />
            <Shades
              label={label}
              stops={BRAND_SHADE_STOPS}
              baseStop={BRAND_BASE_STOP}
              shades={isHexColor(brand[name]) ? generateBrandShades(brand[name]) : undefined}
            />
          </Box>
        ))}
      </Section>
      <Section title="Neutral">
        <Box sx={COLOR_ROW_SX}>
          <ColorInput name="colors.neutral.black" label="Black" isRequired isEditing={isEditing} />
          <Shades
            label="Black"
            stops={NEUTRAL_SHADE_STOPS}
            baseStop={NEUTRAL_BASE_STOP}
            shades={isHexColor(neutral.black) ? generateNeutralShades(neutral.black) : undefined}
          />
        </Box>
      </Section>
      <Section title="Utility">
        {UTILITY_COLOR_FIELDS.map(({ name, label }) => (
          <ColorInput
            key={name}
            name={`colors.utility.${name}`}
            label={label}
            isRequired
            isEditing={isEditing}
          />
        ))}
      </Section>
    </Box>
  );
};
